import { create } from 'zustand';

/**
 * Workspace filter context shared across Chart, Dashboard and AI tabs.
 *
 * The AI assistant reads this so its system prompt can describe the user's
 * current view (active SQL, year range, category) and answer in context.
 *
 * All fields are optional — an empty object is the "no context" default.
 */
export interface WorkspaceFilterContext {
  yearRange?: [number, number];
  category?: string;
  activeSql?: string;
}

/** An AI answer opened in the chart builder (/ai-assistant) — there the person changes its type / colour. */
export interface PendingChart {
  sql: string;
  chartType: string;
  title: string;
  /** set on every hand-over — the builder reruns when it changes */
  key?: number;
}

interface WorkspaceState {
  filterContext: WorkspaceFilterContext;
  pendingChart: PendingChart | null;
  setPendingChart: (c: PendingChart | null) => void;
  setFilterContext: (
    next: WorkspaceFilterContext | ((prev: WorkspaceFilterContext) => WorkspaceFilterContext),
  ) => void;
  resetFilterContext: () => void;
}

export const useWorkspaceStore = create<WorkspaceState>()((set) => ({
  filterContext: {},
  pendingChart: null,
  setPendingChart: (c) => set({ pendingChart: c ? { ...c, key: Date.now() } : null }),
  setFilterContext: (next) =>
    set((s) => ({
      filterContext: typeof next === 'function' ? next(s.filterContext) : next,
    })),
  resetFilterContext: () => set({ filterContext: {} }),
}));
